import React, { useState, useEffect } from "react";
import axios from "axios";
import "../Styles/ChangePassword.css";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Loader from "../Components/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ChangePassword() {
    const [formData, setFormData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [passwordStrength, setPasswordStrength] = useState(0);

    const [showPassword, setShowPassword] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            toast.error("Unauthorized access. Please log in.");
            setLoading(false);
            return;
        }

        const fetchUserProfile = async () => {
            try {
                await axios.get("https://api.uniums.com/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } catch (err) {
                toast.error("Failed to fetch user profile");
            } finally {
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === "newPassword") {
            setPasswordStrength(checkPasswordStrength(value));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword((prevState) => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const checkPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 5) strength += 1;
        if (password.length >= 8) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[a-z]/.test(password)) strength += 1;
        if (/\d/.test(password)) strength += 1;
        if (/[@$!%*?&]/.test(password)) strength += 1;
        return strength;
    };

    const getStrengthColor = () => {
        if (passwordStrength <= 2) return "red";
        if (passwordStrength <= 4) return "yellow";
        return "green";
    };

    const validatePassword = (password) => {
        return passwordStrength === 6;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validatePassword(formData.newPassword)) {
            toast.error("Password must be strong (meet all requirements).");
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            toast.error("New password and confirm password do not match.");
            return;
        }

        setSubmitting(true);

        try {
            const token = localStorage.getItem("authToken");
            await axios.post(
                "https://api.uniums.com/api/change-password",
                {
                    currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            toast.success("Password updated successfully!");
            setFormData({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
            });
            setPasswordStrength(0);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update password");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="change-password-container">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="change-password-card">
                <h2 className="change-password-title">Change Password</h2>
                <form onSubmit={handleSubmit} className="change-password-form">
                    <div className="change-password-field">
                        <label>Current Password</label>
                        <div className="change-password-input-wrapper">
                            <input
                                type={showPassword.currentPassword ? "text" : "password"}
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                required
                            />
                            <span className="change-password-icon" onClick={() => togglePasswordVisibility("currentPassword")}>
                                {showPassword.currentPassword ? <VisibilityOff /> : <Visibility />}
                            </span>
                        </div>
                    </div>

                    <div className="change-password-field">
                        <label>New Password</label>
                        <div className="change-password-input-wrapper">
                            <input
                                type={showPassword.newPassword ? "text" : "password"}
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                required
                            />
                            <span className="change-password-icon" onClick={() => togglePasswordVisibility("newPassword")}>
                                {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                            </span>
                        </div>
                        <small className="password-requirements">
                            Password must be between <strong>5 to 10 characters</strong> and include at least one uppercase letter, one lowercase letter, one number, and one special character (e.g., <strong>Abcd@543</strong>).
                        </small>

                        {/* Password Strength Progress Bar */}
                        <div className="password-strength-bar">
                            <div className="password-strength" style={{ width: `${(passwordStrength / 6) * 100}%`, backgroundColor: getStrengthColor() }}></div>
                        </div>
                    </div>

                    <div className="change-password-field">
                        <label>Confirm New Password</label>
                        <div className="change-password-input-wrapper">
                            <input
                                type={showPassword.confirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                            />
                            <span className="change-password-icon" onClick={() => togglePasswordVisibility("confirmPassword")}>
                                {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                            </span>
                        </div>
                    </div>

                    <button type="submit" className="change-password-button" disabled={submitting}>
                        {submitting ? <Loader /> : "Update Password"}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ChangePassword;